import * as THREE from 'three';
import OBJECT_NAMES, {
    TEXT_SPRITE_CONSTANT,
} from 'components/customer/RoomPlanner/constants/ObjectNameMeasurement';
import {
    formatMmLabel,
    hasDecimalFromLabels,
} from 'components/customer/RoomPlanner/helpers/formatMeasurementLabel';
import {
    getActualMeasurementExtents,
    MeasurementConfig,
    MeasurementData,
} from 'components/customer/RoomPlanner/helpers/measurement/types';
import {
    CABINET_CARCASE_CENTER_X_NUDGE,
    elevationHeightsMatch,
    getOccupiedProductSpan,
    getWallType,
} from 'components/customer/RoomPlanner/helpers/measurement/measurementData';

/** Horizontal step between elevation height columns, in scene units. */
export const HEIGHT_COLUMN_SPACING = 110;

/**
 * How far a floor or ceiling label sits in from the wall end.
 * Long gaps keep the number near the floor or the ceiling instead of
 * the middle of the wall, where it would cover another cabinet's size.
 */
const HEIGHT_GAP_LABEL_INSET = 36;

/**
 * Minimum visual overlap (scene units) before two measurement ticks are treated
 * as the same boundary.
 */
const VISUAL_OVERLAP_CLUSTER_EPSILON = 0.1;

const disableArrowDepthTest = (arrow: THREE.ArrowHelper) => {
    arrow.traverse((child) => {
        if (!(child instanceof THREE.Mesh || child instanceof THREE.Line)) {
            return;
        }
        const material = child.material as THREE.Material | THREE.Material[];
        const materials = Array.isArray(material) ? material : [material];
        materials.forEach((item) => {
            item.depthTest = false;
            item.depthWrite = false;
            item.needsUpdate = true;
        });
        child.renderOrder = TEXT_SPRITE_CONSTANT - 1;
    });
};

/**
 * Flush-snapped placeholder + cabinet overlap by half the carcase X nudge in
 * measurement space even though they only touch. Require more than that before
 * merging into a mixed/stacked wall measurement. Real stacked overlaps (base
 * under a wider upper) are tens of scene units, so they still merge.
 */
const VISUAL_OVERLAP_MERGE_THRESHOLD =
    CABINET_CARCASE_CENTER_X_NUDGE / 2 + VISUAL_OVERLAP_CLUSTER_EPSILON;

/**
 * Normalized start/end for span math. getActualMeasurementExtents keeps raw
 * values; mixed totals must use min/max on both edges.
 * @param {MeasurementData} measurement - Measurement row
 * @return {{start: number, end: number}} Left/right bounds in scene units
 */
const getNormalizedMeasurementBounds = (
    measurement: MeasurementData
): {start: number; end: number} => {
    const {start, end} = getActualMeasurementExtents(measurement);
    return {
        start: start,
        end: end,
    };
};

/**
 * Snap a mixed span to mm for labeling without changing geometry.
 * Rounds the span once (not each edge) so float noise does not add a phantom
 * +1mm, while still preserving real fractional sizes when needed.
 * @param {number} scaledWidth - Span in scene units
 * @param {number} scale - Room scale
 * @param {boolean} hasDecimal - Preserve one decimal place when labels need it
 * @return {number} Snapped width in scene units
 */
const snapMixedScaledWidth = (
    scaledWidth: number,
    scale: number,
    hasDecimal: boolean
): number => {
    if (!scale || !Number.isFinite(scaledWidth) || scaledWidth <= 0) {
        return scaledWidth;
    }
    const mm = scaledWidth / scale;
    const snappedMm = hasDecimal
        ? Math.round(mm * 10) / 10
        : // Tiny downward bias so values like 1800.5000002 (float) stay 1800
          // instead of intermittent 1801, without pulling real 1800.6 down.
          Math.round(mm - 1e-7);
    return snappedMm * scale;
};

/**
 * Resolves collisions between measurements by marking overlaps for compact rendering.
 * @param {MeasurementData[]} measurements - Array of measurements to process
 * @param {MeasurementConfig} config - Configuration for measurement generation
 * @return {MeasurementData[]} Measurements with compact overlap flags
 */
export const resolveCollisions = (
    measurements: MeasurementData[],
    config: MeasurementConfig
): MeasurementData[] => {
    // Use a compact vertical spacing tied loosely to wall height so arrows
    // remain close to the wall surface
    const spacing = 50;
    const margin = 5; // Horizontal tolerance before considering as no-overlap

    const isFrontElevation =
        config.isForPrinting && config.previewPosition === 'FRONT';
    const isLeftElevation =
        config.isForPrinting && config.previewPosition === 'RIGHT_LEVELED';
    const isRightElevation =
        config.isForPrinting && config.previewPosition === 'LEFT_LEVELED';

    const isHeightBasis =
        (isFrontElevation &&
            [OBJECT_NAMES.LEFT_WALL, OBJECT_NAMES.LEFT_WIDTH_WALL].includes(
                config.wallRefId
            )) ||
        (isLeftElevation &&
            [OBJECT_NAMES.FRONT_WALL, OBJECT_NAMES.LEFT_DEPTH_WALL].includes(
                config.wallRefId
            )) ||
        (isRightElevation &&
            [OBJECT_NAMES.BACK_WALL, OBJECT_NAMES.RIGHT_WIDTH_WALL].includes(
                config.wallRefId
            ));

    // Sort by start position; break ties by higher priority first so lower
    // priority items tend to be pushed upward when conflicts occur
    const sortedMeasurements = [...measurements].sort((a, b) => {
        if (a.start !== b.start) return a.start - b.start;
        return (b.priority || 0) - (a.priority || 0);
    });

    // Initialize all measurements to the same baseline height so they start
    // aligned before we begin collision resolution
    const baselineY = config?.wallHeight ?? 0;
    for (const m of sortedMeasurements) {
        if (!isHeightBasis) {
            m.yPosition = baselineY;
        }
    }

    const overlapsHorizontally = (
        left: MeasurementData,
        right: MeasurementData
    ): boolean => {
        return !(
            left.end - margin < right.start || left.start > right.end - margin
        );
    };

    // Assign minimal vertical levels without runaway growth:
    // - Sort by priority descending, then by start asc
    // - For each measurement, choose the lowest level that does not
    //   horizontally overlap with any measurement already placed at that level
    const byPriority = [...sortedMeasurements].sort((a, b) => {
        const pa = a.priority || 0;
        const pb = b.priority || 0;
        if (pa !== pb) return pb - pa;
        if (a.start !== b.start) return a.start - b.start;
        return a.end - b.end;
    });

    type Placed = MeasurementData & {level: number};
    const levels: Placed[][] = [];

    const placed: Placed[] = [];

    const maxLevels = 15; // Prevent arrows from drifting too far from the wall
    for (const m of byPriority) {
        // Find the first existing level that can accept this measurement
        let useLevel = levels.find(
            (level) => !level.some((x) => overlapsHorizontally(x, m))
        );

        // If none fits and we still have room, create a new level
        if (!useLevel) {
            if (levels.length < maxLevels) {
                useLevel = [] as Placed[];
                levels.push(useLevel);
            } else {
                // Otherwise, place in the last level as a fallback
                useLevel = levels.at(-1) ?? [];
                if (levels.length === 0) {
                    levels.push(useLevel);
                }
            }
        }

        const levelIndex = Math.min(levels.indexOf(useLevel), maxLevels - 1);
        const placedItem: Placed = {...m, level: levelIndex};
        placed.push(placedItem);
        useLevel.push(placedItem);
    }
    const isPlanView = config.isForPrinting && config.previewPosition === 'TOP';

    const spacingMap = {
        [OBJECT_NAMES.RIGHT_WIDTH_WALL]: isRightElevation ? 100 : 50,
        [OBJECT_NAMES.LEFT_WIDTH_WALL]: -25,
        [OBJECT_NAMES.RIGHT_DEPTH_WALL]: -25,
        [OBJECT_NAMES.LEFT_DEPTH_WALL]: isLeftElevation ? 100 : 50,
        [OBJECT_NAMES.LEFT_WIDTH2_WALL]: -25,
        [OBJECT_NAMES.RIGHT_WIDTH2_WALL]: -25,
        [OBJECT_NAMES.LEFT_WALL]: -25,
        [OBJECT_NAMES.RIGHT_WALL]: 50,
        [OBJECT_NAMES.BACK_WALL]: 50,
        [OBJECT_NAMES.FRONT_WALL]: -25,
    };

    const zPosBaseline = spacingMap[config.wallRefId] ?? 0;
    const zPosSpacing = spacing * (zPosBaseline > 0 ? 1 : -1);

    const getVisualSpan = (measurement: MeasurementData) => {
        const measurementWidth =
            measurement.actualWidth ??
            measurement.width ??
            Math.abs(measurement.end - measurement.start);
        const center =
            measurement.xPosition ?? (measurement.start + measurement.end) / 2;

        return {
            left: center - measurementWidth / 2,
            right: center + measurementWidth / 2,
            width: measurementWidth,
        };
    };

    const visualSpansOverlap = (
        leftSpan: {left: number; right: number},
        rightSpan: {left: number; right: number}
    ): boolean => {
        const overlapWidth =
            Math.min(leftSpan.right, rightSpan.right) -
            Math.max(leftSpan.left, rightSpan.left);
        return overlapWidth > VISUAL_OVERLAP_MERGE_THRESHOLD;
    };

    const getOverlappingCabinetsInCluster = (
        clusterItems: Placed[]
    ): Placed[] => {
        const cabinets = clusterItems.filter((item) => item.type === 'cabinet');
        if (cabinets.length < 2) return [];

        const overlapping = new Set<Placed>();
        for (let i = 0; i < cabinets.length; i++) {
            for (let j = i + 1; j < cabinets.length; j++) {
                const leftCabinet = cabinets.at(i);
                const rightCabinet = cabinets.at(j);
                if (!leftCabinet || !rightCabinet) {
                    continue;
                }
                if (
                    visualSpansOverlap(
                        getVisualSpan(leftCabinet),
                        getVisualSpan(rightCabinet)
                    )
                ) {
                    overlapping.add(leftCabinet);
                    overlapping.add(rightCabinet);
                }
            }
        }

        return cabinets.filter((cabinet) => overlapping.has(cabinet));
    };

    const compactOverlappingWidthMeasurements = (
        items: Placed[]
    ): MeasurementData[] => {
        const sortedByVisualStart = [...items].sort((a, b) => {
            const aSpan = getVisualSpan(a);
            const bSpan = getVisualSpan(b);
            if (aSpan.left !== bSpan.left) return aSpan.left - bSpan.left;
            return aSpan.right - bSpan.right;
        });

        const compacted: MeasurementData[] = [];
        let cluster: Placed[] = [];
        let clusterLeft = Infinity;
        let clusterRight = -Infinity;

        const pushNormalMeasurement = (item: Placed) => {
            compacted.push({
                ...item,
                compactOnOverlap: false,
                yPosition: baselineY,
                ...(isPlanView ? {zPosition: zPosBaseline} : {}),
            });
        };

        const flushCluster = () => {
            if (!cluster.length) return;

            if (cluster.length === 1) {
                pushNormalMeasurement(cluster[0]);
                cluster = [];
                clusterLeft = Infinity;
                clusterRight = -Infinity;
                return;
            }

            const overlappingCabinets =
                getOverlappingCabinetsInCluster(cluster);
            if (overlappingCabinets.length < 2) {
                cluster.forEach((item) => pushNormalMeasurement(item));
                cluster = [];
                clusterLeft = Infinity;
                clusterRight = -Infinity;
                return;
            }

            const overlappingCabinetIds = new Set(
                overlappingCabinets.map((item) => item.id)
            );

            cluster.forEach((item) => {
                if (!overlappingCabinetIds.has(item.id)) {
                    pushNormalMeasurement(item);
                }
            });

            const visualSpans = overlappingCabinets.map(getVisualSpan);
            const visualStart = Math.min(...visualSpans.map(({left}) => left));
            const visualEnd = Math.max(...visualSpans.map(({right}) => right));
            const mixedDisplayWidth = visualEnd - visualStart;

            const hasDecimal = hasDecimalFromLabels(
                overlappingCabinets.map(({label}) => label)
            );
            const occupiedSpan = getOccupiedProductSpan(
                overlappingCabinets,
                config.scale || 1
            );
            const actualBounds = overlappingCabinets.map(
                getNormalizedMeasurementBounds
            );
            const actualStart = Math.min(
                ...actualBounds.map(({start}) => start)
            );
            const actualEnd = Math.max(...actualBounds.map(({end}) => end));
            const mixedActualScaledWidth =
                occupiedSpan.width > 0
                    ? occupiedSpan.width
                    : config.scale && actualEnd > actualStart
                      ? snapMixedScaledWidth(
                            actualEnd - actualStart,
                            config.scale,
                            hasDecimal
                        )
                      : actualEnd - actualStart;

            const mainMeasurement = [...overlappingCabinets].sort((a, b) => {
                const levelDiff = a.level - b.level;
                if (levelDiff !== 0) return levelDiff;
                const priorityDiff = (b.priority || 0) - (a.priority || 0);
                if (priorityDiff !== 0) return priorityDiff;
                return a.start - b.start;
            })[0];

            const mixedLabel =
                config.scale && mixedActualScaledWidth > 0
                    ? formatMmLabel(
                          mixedActualScaledWidth,
                          config.scale,
                          hasDecimal
                      )
                    : mainMeasurement.label;

            const sortedCabinetSpans = overlappingCabinets
                .map((cabinet) => ({cabinet, span: getVisualSpan(cabinet)}))
                .sort((a, b) => a.span.left - b.span.left);
            const compactInternalBoundaries: number[] = [];
            const pushUniqueBoundary = (x: number) => {
                if (
                    compactInternalBoundaries.some(
                        (existing) =>
                            Math.abs(existing - x) <
                            VISUAL_OVERLAP_CLUSTER_EPSILON
                    )
                ) {
                    return;
                }
                compactInternalBoundaries.push(x);
            };
            for (let i = 0; i < sortedCabinetSpans.length - 1; i++) {
                const currentSpan = sortedCabinetSpans.at(i);
                const nextSpan = sortedCabinetSpans.at(i + 1);
                if (!currentSpan || !nextSpan) {
                    continue;
                }
                const {span: leftSpan} = currentSpan;
                const {span: rightSpan} = nextSpan;
                if (!visualSpansOverlap(leftSpan, rightSpan)) {
                    continue;
                }
                // One vertical tick on each cabinet's inside edge (not one at overlap center).
                pushUniqueBoundary(leftSpan.right);
                pushUniqueBoundary(rightSpan.left);
            }

            // When one cabinet's start/end exactly align with the cluster
            // total (both edges come from the same cabinet), the mixed total
            // is redundant — drop it and put that cabinet on the baseline so
            // wall measurements stay vertically compact and use the real
            // cabinet label (avoids ~4mm drift from aggregated min/max).
            const spansWholeCluster = (span: {
                left: number;
                right: number;
            }): boolean => span.left <= visualStart && span.right >= visualEnd;
            const widestEntry = sortedCabinetSpans.reduce((widest, entry) =>
                entry.span.width > widest.span.width ? entry : widest
            );
            const maxProductWidth = Math.max(
                ...overlappingCabinets.map(
                    (cabinet) =>
                        cabinet.actualWidth ?? getVisualSpan(cabinet).width
                )
            );
            const isStackedCluster =
                occupiedSpan.width <= maxProductWidth + 1e-4;
            const spanningEntry =
                sortedCabinetSpans.find(({span}) => spansWholeCluster(span)) ??
                (isStackedCluster ? widestEntry : undefined);
            const totalIsRedundant = Boolean(spanningEntry);

            if (!totalIsRedundant) {
                compacted.push({
                    ...mainMeasurement,
                    id: `mixed-${mainMeasurement.id}`,
                    label: mixedLabel,
                    start: actualStart,
                    end: actualEnd,
                    xPosition: (visualStart + visualEnd) / 2,
                    width: mixedDisplayWidth,
                    actualWidth: mixedActualScaledWidth,
                    compactLabelScaledWidth: mixedActualScaledWidth,
                    compactOnOverlap: true,
                    compactMixedMeasurement: true,
                    compactInternalBoundaries,
                    yPosition: baselineY,
                    ...(isPlanView ? {zPosition: zPosBaseline} : {}),
                });
            }

            // Each overlapping cabinet keeps its original full-size arrow
            // measurement, stacked above the baseline: the baseline holds the
            // total, or the spanning cabinet when the total is skipped.
            const pushCabinetRow = (
                {cabinet, span}: (typeof sortedCabinetSpans)[number],
                stackLevel: number
            ) => {
                compacted.push({
                    ...cabinet,
                    id: `segment-${cabinet.id}`,
                    xPosition: (span.left + span.right) / 2,
                    width: span.width,
                    compactOnOverlap: false,
                    yPosition: baselineY + stackLevel * spacing,
                    ...(isPlanView
                        ? {
                              zPosition:
                                  zPosBaseline + stackLevel * zPosSpacing,
                          }
                        : {}),
                });
            };

            const stackedEntries = (
                totalIsRedundant && spanningEntry
                    ? sortedCabinetSpans.filter(
                          (entry) => entry !== spanningEntry
                      )
                    : sortedCabinetSpans
            ).filter((entry) => {
                if (!isStackedCluster || !config.scale) {
                    return true;
                }
                const entryMm = Math.round(entry.span.width / config.scale);
                const spanningMm = spanningEntry
                    ? Math.round(spanningEntry.span.width / config.scale)
                    : null;
                return entryMm !== spanningMm;
            });

            if (totalIsRedundant && spanningEntry) {
                pushCabinetRow(spanningEntry, 0);
            }

            // First-fit stacking: reuse the lowest row above the baseline
            // whose already-placed arrows don't horizontally overlap this
            // one; only open a new row above when every lower row collides.
            const stackRows: {left: number; right: number}[][] = [];
            for (const entry of stackedEntries) {
                let rowIndex = stackRows.findIndex(
                    (row) =>
                        !row.some((placedSpan) =>
                            visualSpansOverlap(placedSpan, entry.span)
                        )
                );
                if (rowIndex === -1) {
                    rowIndex = stackRows.length;
                    stackRows.push([]);
                }
                stackRows.at(rowIndex)?.push(entry.span);
                pushCabinetRow(entry, rowIndex + 1);
            }

            cluster = [];
            clusterLeft = Infinity;
            clusterRight = -Infinity;
        };

        for (const item of sortedByVisualStart) {
            const span = getVisualSpan(item);
            const overlapsCluster =
                cluster.length > 0 &&
                visualSpansOverlap(span, {
                    left: clusterLeft,
                    right: clusterRight,
                });

            if (!cluster.length || overlapsCluster) {
                cluster.push(item);
                clusterLeft = Math.min(clusterLeft, span.left);
                clusterRight = Math.max(clusterRight, span.right);
                continue;
            }

            flushCluster();
            cluster.push(item);
            clusterLeft = span.left;
            clusterRight = span.right;
        }

        flushCluster();

        return compacted;
    };

    if (!isHeightBasis) {
        return compactOverlappingWidthMeasurements(placed).sort(
            (a, b) => a.start - b.start
        );
    }

    // One column per cabinet size and vertical position. Matching cabinets
    // share that column, so the size label is drawn once. Floor and ceiling
    // gaps stay on the same column. The lowest, shortest group is nearest
    // the wall; later groups step out far enough that the labels clear.
    const heightZAway = HEIGHT_COLUMN_SPACING * (zPosBaseline > 0 ? 1 : -1);

    const cabinetRows = placed.filter((item) => item.type === 'cabinet');
    const groupOrder: MeasurementData[][] = [];
    [...cabinetRows]
        .sort((left, right) => {
            if (left.start !== right.start) {
                return left.start - right.start;
            }
            const spanDelta =
                Math.abs(left.end - left.start) -
                Math.abs(right.end - right.start);
            if (spanDelta !== 0) {
                return spanDelta;
            }
            return (left.label ?? '').localeCompare(right.label ?? '');
        })
        .forEach((item) => {
            const group = groupOrder.find((members) =>
                elevationHeightsMatch(members[0], item)
            );
            if (group) {
                group.push(item);
                return;
            }
            groupOrder.push([item]);
        });

    const zByGroup = new Map<MeasurementData[], number>();
    groupOrder.forEach((group, index) => {
        zByGroup.set(group, zPosBaseline + heightZAway * index);
    });

    const groupByCabinetId = new Map<string, MeasurementData[]>();
    groupOrder.forEach((group) => {
        group.forEach((item) => {
            if (item.cabinetId) {
                groupByCabinetId.set(item.cabinetId, group);
            }
            groupByCabinetId.set(item.id, group);
        });
    });

    const final = placed
        .map((item) => {
            const cabinetGroup = item.cabinetId
                ? groupByCabinetId.get(item.cabinetId)
                : undefined;
            const ownGroup =
                item.type === 'cabinet'
                    ? groupOrder.find((members) =>
                          elevationHeightsMatch(members[0], item)
                      )
                    : undefined;
            const zPosition =
                (ownGroup ? zByGroup.get(ownGroup) : undefined) ??
                (cabinetGroup ? zByGroup.get(cabinetGroup) : undefined) ??
                zPosBaseline;

            return {
                ...item,
                compactOnOverlap: false,
                zPosition,
            };
        })
        .sort((a, b) => a.start - b.start);

    return final;
};

/**
 * Vertical position of an elevation height label. Cabinet size stays on the
 * cabinet. Floor and ceiling numbers sit in from the wall ends so a long
 * gap does not print its label across another cabinet.
 * @param {MeasurementData} measurement - Height or gap row
 * @return {number} Label Y in scene units
 */
export const getHeightMeasurementLabelY = (
    measurement: MeasurementData
): number => {
    const start = Math.min(measurement.start, measurement.end);
    const end = Math.max(measurement.start, measurement.end);
    const span = end - start;
    if (!(span > 0)) {
        return measurement.yPosition;
    }

    const inset = Math.min(span / 2, HEIGHT_GAP_LABEL_INSET);
    const id = measurement.id ?? '';
    if (id.startsWith('gap-ceiling')) {
        return end - inset;
    }
    if (id.startsWith('gap-floor') || id.startsWith('gap-toe')) {
        return start + inset;
    }
    return (start + end) / 2;
};

// Cache for label sprite materials to avoid recreating canvases/textures per measurement
const labelMaterialCache = new Map<string, THREE.SpriteMaterial>();

interface MeasurementObject {
    leftArrow: THREE.ArrowHelper;
    rightArrow: THREE.ArrowHelper;
    sprite: THREE.Sprite;
    leftGuideline?: THREE.Line;
    rightGuideline?: THREE.Line;
    leftPartitionTick?: THREE.Line;
    rightPartitionTick?: THREE.Line;
}

/**
 * Drop sprites/arrows for empty labels, numeric zero labels, or degenerate spans (corner "0" clutter).
 * @param {MeasurementData} measurement - Measurement row before building Three.js visuals
 * @return {boolean} True if this row should not get a sprite/arrow pair
 */
const shouldSkipMeasurementVisual = (measurement: MeasurementData): boolean => {
    const trimmed = (measurement.label ?? '').trim();
    if (!trimmed) {
        return true;
    }
    const labelNum = Number(trimmed);
    if (Number.isFinite(labelNum) && labelNum === 0) {
        return true;
    }
    const w = measurement.width;
    if (w !== undefined && Number.isFinite(w) && Math.abs(w) < 1e-5) {
        return true;
    }
    return false;
};

/**
 * Shrinks a CSS canvas font string while keeping weight and family intact.
 * @param {string} font - CSS font string such as "bold 40px Arial"
 * @return {string} Compact font string
 */
const getCompactLabelFont = (font: string): string =>
    font
        .split(' ')
        .map((part) => {
            if (!part.endsWith('px')) {
                return part;
            }

            const fontSize = Number(part.slice(0, -2));
            if (!Number.isFinite(fontSize)) {
                return part;
            }

            const compactSize = Math.max(14, Math.round(fontSize * 0.8));
            return `${compactSize}px`;
        })
        .join(' ');

/**
 * Returns the real scaled measurement width for compact overlap labels.
 * @param {MeasurementData} measurement - Measurement row
 * @return {number} Width in scaled scene units
 */
const getCompactMeasurementScaledWidth = (
    measurement: MeasurementData
): number => {
    if (
        measurement.compactLabelScaledWidth &&
        Number.isFinite(measurement.compactLabelScaledWidth)
    ) {
        return measurement.compactLabelScaledWidth;
    }

    const actualSpan =
        measurement.actualSize &&
        Math.abs(measurement.actualSize.end - measurement.actualSize.start);
    if (actualSpan && Number.isFinite(actualSpan)) {
        return actualSpan;
    }

    const explicitWidth = measurement.actualWidth ?? measurement.width;
    if (explicitWidth && Number.isFinite(explicitWidth)) {
        return Math.abs(explicitWidth);
    }

    return Math.abs(measurement.end - measurement.start);
};

/**
 * Formats the compact overlap label from the measured start/end or width.
 * @param {MeasurementData} measurement - Measurement row
 * @param {MeasurementConfig} config - Measurement config
 * @return {string} Label text
 */
const getMeasurementLabelText = (
    measurement: MeasurementData,
    config: MeasurementConfig
): string => {
    if (measurement.compactSegmentLabel) {
        return measurement.label;
    }

    if (!measurement.compactMixedMeasurement || !config.scale) {
        return measurement.label;
    }

    const scaledWidth = getCompactMeasurementScaledWidth(measurement);
    if (!Number.isFinite(scaledWidth) || scaledWidth <= 0) {
        return measurement.label;
    }

    // Mixed span is computed from scene/world bounds so it can be fractional in mm
    // (e.g. 1576.3). Round to integer when cabinet labels are whole numbers (900).
    const hasDecimal = hasDecimalFromLabels([measurement.label]);
    return formatMmLabel(scaledWidth, config.scale, hasDecimal);
};

type MeasurementLabelSprite = {
    sprite: THREE.Sprite;
    text: string;
    isSegmentLabel: boolean;
    isMixedMeasurement: boolean;
    basePosition: THREE.Vector3;
};

const LABEL_SPRITE_OVERLAP_MARGIN = 2;
const LABEL_SPRITE_X_NUDGE = 28;
const LABEL_SPRITE_MAX_NUDGE_ATTEMPTS = 12;

const getMeasurementLabelRowKey = (entry: MeasurementLabelSprite): string =>
    entry.isSegmentLabel ? 'segment' : 'mixed';

/**
 * Planar bounds from sprite position/scale; tighter than Box3 so stacked rows do not false-positive.
 * @param {MeasurementLabelSprite} entry - Label sprite entry
 * @return {{minX: number, maxX: number, minY: number, maxY: number}} 2D bounds
 */
const getMeasurementLabelBounds2D = (
    entry: MeasurementLabelSprite
): {minX: number; maxX: number; minY: number; maxY: number} => {
    const {sprite, text, isSegmentLabel} = entry;
    const {x, y} = sprite.position;
    const halfH = sprite.scale.y * 0.45;
    const charWidth = isSegmentLabel ? 7 : 10;
    const estimatedHalfW = Math.max(
        (text.length * charWidth) / 2,
        sprite.scale.x * 0.2
    );
    const halfW = Math.min(sprite.scale.x / 2, estimatedHalfW + 4);

    return {
        minX: x - halfW,
        maxX: x + halfW,
        minY: y - halfH,
        maxY: y + halfH,
    };
};

const measurementLabelBoundsOverlap = (
    a: MeasurementLabelSprite,
    b: MeasurementLabelSprite
): boolean => {
    if (getMeasurementLabelRowKey(a) !== getMeasurementLabelRowKey(b)) {
        return false;
    }

    const boxA = getMeasurementLabelBounds2D(a);
    const boxB = getMeasurementLabelBounds2D(b);

    return !(
        boxA.maxX + LABEL_SPRITE_OVERLAP_MARGIN < boxB.minX ||
        boxA.minX - LABEL_SPRITE_OVERLAP_MARGIN > boxB.maxX ||
        boxA.maxY + LABEL_SPRITE_OVERLAP_MARGIN < boxB.minY ||
        boxA.minY - LABEL_SPRITE_OVERLAP_MARGIN > boxB.maxY
    );
};

const adjustDifferentTextMeasurementLabels = (
    entries: MeasurementLabelSprite[]
): void => {
    const visibleEntries = entries.filter(({sprite}) => sprite.visible);
    visibleEntries.sort((a, b) => a.basePosition.x - b.basePosition.x);

    const placedEntries: MeasurementLabelSprite[] = [];

    visibleEntries.forEach((entry) => {
        entry.sprite.position.copy(entry.basePosition);

        for (
            let attempt = 0;
            attempt < LABEL_SPRITE_MAX_NUDGE_ATTEMPTS;
            attempt++
        ) {
            const overlappingEntry = placedEntries.find(
                (other) =>
                    other.text !== entry.text &&
                    measurementLabelBoundsOverlap(entry, other)
            );

            if (!overlappingEntry) {
                placedEntries.push(entry);
                return;
            }

            const position = entry.sprite.position;
            const otherPosition = overlappingEntry.sprite.position;

            // Same row only (mixed vs segment never compared); spread horizontally.
            position.x +=
                position.x >= otherPosition.x
                    ? LABEL_SPRITE_X_NUDGE
                    : -LABEL_SPRITE_X_NUDGE;
        }

        placedEntries.push(entry);
    });
};

const resolveMeasurementLabelOverlaps = (
    entries: MeasurementLabelSprite[]
): void => {
    if (entries.length < 2) {
        return;
    }

    adjustDifferentTextMeasurementLabels(entries);
};

/**
 * Centralized function to generate all visual measurement elements from measurement data
 * @param {MeasurementData[]} measurementData - Array of measurement data
 * @param {MeasurementConfig} config - Configuration for measurement generation
 * @param {THREE.Group} parentGroup - Parent group to add measurements to
 * @param {Function} handleArrowsAndGroup - Callback function for arrow handling
 * @param {THREE.Group<THREE.Object3DEventMap>[]} cabinetGroupList - Cabinet groups for position calculation
 * @return {THREE.Group} The parent group with measurements added
 */
export const generateMeasurementVisuals = (
    measurementData: MeasurementData[],
    config: MeasurementConfig,
    parentGroup: THREE.Group,
    handleArrowsAndGroup: (
        arrows: (THREE.ArrowHelper | THREE.SpriteMaterial | THREE.Line)[],
        arrowGroup?: THREE.Group
    ) => void
): THREE.Group => {
    const isPlanView = config.isForPrinting && config.previewPosition === 'TOP';
    const isFrontElevation =
        config.isForPrinting && config.previewPosition === 'FRONT';
    const isLeftElevation =
        config.isForPrinting && config.previewPosition === 'RIGHT_LEVELED';
    const isRightElevation =
        config.isForPrinting && config.previewPosition === 'LEFT_LEVELED';

    const {
        isLeftWall,
        isRightWall,
        isFrontWall,
        isBackWall,
        isLeftWidthWall,
        isLeftDepthWall,
        isRightDepthWall,
        isRightWidthWall,
        isRightWidth2Wall,
        isLeftWidth2Wall,
    } = getWallType(config.wallRefId);

    const displayHeightOnWall =
        (isFrontElevation && (isLeftWall || isLeftWidthWall)) ||
        (isLeftElevation && (isFrontWall || isLeftDepthWall)) ||
        (isRightElevation && (isBackWall || isRightWidthWall));

    const finalMeasurements = resolveCollisions(measurementData, config);

    const arrowObjects: (
        THREE.ArrowHelper | THREE.SpriteMaterial | THREE.Line
    )[] = [];
    const measurementMap = new Map<string, MeasurementObject>();
    const labelSpriteEntries: MeasurementLabelSprite[] = [];

    // Arrow styling constants
    const arrowHeadLength = 8;
    const arrowHeadWidth = 8;

    finalMeasurements.forEach((measurement) => {
        try {
            if (shouldSkipMeasurementVisual(measurement)) {
                return;
            }
            // Use pre-calculated positions or fallback to simple calculation
            const xPosition =
                measurement.xPosition ??
                (measurement.start + measurement.end) / 2;
            const width =
                measurement.actualWidth ??
                measurement.width ??
                Math.abs(measurement.end - measurement.start);
            const isMixedMeasurement =
                measurement.compactMixedMeasurement === true;
            const isSegmentLabel = measurement.compactSegmentLabel === true;
            const isCompactMeasurement = isMixedMeasurement || isSegmentLabel;
            const labelText = getMeasurementLabelText(measurement, config);
            const labelFont = isSegmentLabel
                ? getCompactLabelFont(config.defaultLabelFontSize)
                : config.defaultLabelFontSize;

            // Calculate left and right positions for guidelines
            const leftPos = xPosition - width / 2;
            const rightPos = xPosition + width / 2;

            // Create label sprite (with material cache)
            const cacheKey = `${labelText}|${labelFont}|${measurement.colour}`;
            let spriteMaterial = labelMaterialCache.get(cacheKey);
            if (!spriteMaterial) {
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');
                if (!context) return;

                canvas.width = 156;
                canvas.height = 64;
                context.fillStyle = measurement.colour;
                context.font = labelFont;
                context.textAlign = 'center';
                context.textBaseline = 'middle';
                context.clearRect(0, 0, canvas.width, canvas.height);
                context.fillText(
                    labelText,
                    canvas.width / 2,
                    canvas.height / 2
                );

                const texture = new THREE.CanvasTexture(canvas);
                spriteMaterial = new THREE.SpriteMaterial({
                    map: texture,
                    transparent: true,
                    depthTest: false,
                    depthWrite: false,
                });
                labelMaterialCache.set(cacheKey, spriteMaterial);
            }

            let spriteMaterialCopy = spriteMaterial;
            let zPosAdjustments = 0;
            if (isPlanView) {
                spriteMaterialCopy = spriteMaterial.clone();
                if (isLeftWidthWall || isLeftWall) {
                    // add flag for printing only and top view only
                    spriteMaterialCopy.rotation = Math.PI / 2;
                    zPosAdjustments = -20;
                } else if (
                    isRightDepthWall ||
                    isRightWall ||
                    isLeftWidth2Wall
                ) {
                    // add flag for printing only and top view only
                    spriteMaterialCopy.rotation = Math.PI / 2;
                    zPosAdjustments = -25;
                    if (isRightWall) {
                        zPosAdjustments = 25;
                    }
                } else if (isRightWidth2Wall || isFrontWall) {
                    zPosAdjustments = -25;
                } else if (isLeftDepthWall) {
                    zPosAdjustments = 25;
                } else {
                    zPosAdjustments = 20;
                }
            } else if (isFrontElevation) {
                spriteMaterialCopy = spriteMaterial.clone();
                if (isLeftWall || isLeftWidthWall) {
                    spriteMaterialCopy.rotation = Math.PI / 2;
                    zPosAdjustments = -20;
                }
            } else if (isLeftElevation) {
                spriteMaterialCopy = spriteMaterial.clone();
                if (isFrontWall || isLeftDepthWall) {
                    spriteMaterialCopy.rotation = Math.PI / 2;
                    zPosAdjustments = isFrontWall ? -20 : 20;
                }
            } else if (isRightElevation) {
                spriteMaterialCopy = spriteMaterial.clone();
                if (isBackWall || isRightWidthWall) {
                    spriteMaterialCopy.rotation = Math.PI / 2;
                    zPosAdjustments = 20;
                }
            }

            const sprite = new THREE.Sprite(spriteMaterialCopy);
            sprite.scale.set(
                isSegmentLabel ? 60 : 100,
                isSegmentLabel ? 30 : 50,
                1
            );
            sprite.renderOrder = TEXT_SPRITE_CONSTANT;
            // Mixed/computed label above the line (default size); cabinet size below.
            const segmentLabelBelowLineOffset = 10;
            const yOffset = isSegmentLabel ? -segmentLabelBelowLineOffset : 20;
            const labelY = displayHeightOnWall
                ? getHeightMeasurementLabelY(measurement)
                : measurement.yPosition + yOffset;
            sprite.position.set(
                xPosition,
                labelY,
                measurement.zPosition + zPosAdjustments
            );
            sprite.userData.measurementType = measurement.type;
            sprite.userData.measurementId = measurement.id;
            sprite.userData.text = labelText;

            // Mixed line: arrow heads on outer edges; segments: line only (no heads).
            const useArrowHeads = !isSegmentLabel;
            const leftArrow = new THREE.ArrowHelper(
                new THREE.Vector3(1, 0, 0).normalize(),
                new THREE.Vector3(
                    xPosition,
                    measurement.yPosition,
                    measurement.zPosition
                ),
                width / 2,
                measurement.colour,
                useArrowHeads ? arrowHeadLength : 0,
                useArrowHeads ? arrowHeadWidth : 0
            );
            leftArrow.userData.measurementId = measurement.id;
            leftArrow.userData.arrowType = 'left';

            const rightArrow = new THREE.ArrowHelper(
                new THREE.Vector3(-1, 0, 0).normalize(),
                new THREE.Vector3(
                    xPosition,
                    measurement.yPosition,
                    measurement.zPosition
                ),
                width / 2,
                measurement.colour,
                useArrowHeads ? arrowHeadLength : 0,
                useArrowHeads ? arrowHeadWidth : 0
            );
            rightArrow.userData.measurementId = measurement.id;
            rightArrow.userData.arrowType = 'right';

            const heightArrowDir = new THREE.Vector3(0, 1, 0).normalize();
            // Match the label plane on print elevations. Cabinet height arrows
            // at z=-15 sit inside the left-wall extrusion; sprites are offset
            // to z=-35, which is why 150/720 numbers printed without arrows.
            const heightArrowZ = measurement.zPosition + zPosAdjustments;
            const heightArrowLength = (measurement.actualWidth ?? width) / 2;
            const upArrow = new THREE.ArrowHelper(
                heightArrowDir,
                new THREE.Vector3(
                    xPosition,
                    measurement.yPosition,
                    heightArrowZ
                ),
                heightArrowLength,
                measurement.colour,
                arrowHeadLength,
                arrowHeadWidth
            );
            upArrow.userData.measurementId = measurement.id;
            upArrow.userData.arrowType = 'up';
            disableArrowDepthTest(upArrow);

            const downArrow = new THREE.ArrowHelper(
                heightArrowDir.clone().negate(),
                new THREE.Vector3(
                    xPosition,
                    measurement.yPosition,
                    heightArrowZ
                ),
                heightArrowLength,
                measurement.colour,
                arrowHeadLength,
                arrowHeadWidth
            );
            downArrow.userData.measurementId = measurement.id;
            downArrow.userData.arrowType = 'down';
            disableArrowDepthTest(downArrow);

            // Create guidelines if requested
            let leftGuideline: THREE.Line | undefined;
            let rightGuideline: THREE.Line | undefined;
            let leftPartitionTick: THREE.Line | undefined;
            let rightPartitionTick: THREE.Line | undefined;
            const internalPartitionTicks: THREE.Line[] = [];

            if (measurement.showGuidelines) {
                const createGuideline = (xPos: number) => {
                    const points = [
                        new THREE.Vector3(
                            xPos,
                            -config.wallHeight / 2 + 290,
                            measurement.zPosition
                        ),
                        new THREE.Vector3(
                            xPos,
                            measurement.yPosition,
                            measurement.zPosition
                        ),
                    ];
                    const lineGeometry =
                        new THREE.BufferGeometry().setFromPoints(points);
                    const lineMaterial = new THREE.LineDashedMaterial({
                        color: 0x333333,
                        dashSize: 5,
                        gapSize: 5,
                        opacity: 0.7,
                        transparent: true,
                    });
                    const line = new THREE.Line(lineGeometry, lineMaterial);
                    line.computeLineDistances();
                    return line;
                };

                leftGuideline = createGuideline(leftPos);
                rightGuideline = createGuideline(rightPos);
                leftGuideline.visible = measurement.showGuidelines;
                rightGuideline.visible = measurement.showGuidelines;
            }

            const createPartitionTick = (xPos: number) => {
                const tickLength = 16;
                const halfTick = tickLength / 2;
                const points = isPlanView
                    ? [
                          new THREE.Vector3(
                              xPos,
                              measurement.yPosition,
                              measurement.zPosition - halfTick
                          ),
                          new THREE.Vector3(
                              xPos,
                              measurement.yPosition,
                              measurement.zPosition + halfTick
                          ),
                      ]
                    : [
                          new THREE.Vector3(
                              xPos,
                              measurement.yPosition - halfTick,
                              measurement.zPosition
                          ),
                          new THREE.Vector3(
                              xPos,
                              measurement.yPosition + halfTick,
                              measurement.zPosition
                          ),
                      ];
                const lineGeometry = new THREE.BufferGeometry().setFromPoints(
                    points
                );
                const lineMaterial = new THREE.LineBasicMaterial({
                    color: measurement.colour,
                    depthTest: false,
                    depthWrite: false,
                });
                const line = new THREE.Line(lineGeometry, lineMaterial);
                line.renderOrder = TEXT_SPRITE_CONSTANT - 1;
                line.userData.measurementId = measurement.id;
                line.userData.isPartitionTick = true;
                return line;
            };

            if (
                isMixedMeasurement &&
                measurement.compactInternalBoundaries?.length
            ) {
                for (const boundaryX of measurement.compactInternalBoundaries) {
                    internalPartitionTicks.push(createPartitionTick(boundaryX));
                }
            }

            // Add elements to parent group
            parentGroup.add(sprite);
            if (isCompactMeasurement) {
                labelSpriteEntries.push({
                    sprite,
                    text: labelText,
                    isSegmentLabel,
                    isMixedMeasurement,
                    basePosition: sprite.position.clone(),
                });
            }
            if (displayHeightOnWall) {
                parentGroup.add(upArrow);
                parentGroup.add(downArrow);
            } else {
                parentGroup.add(leftArrow);
                parentGroup.add(rightArrow);
            }
            if (leftGuideline) parentGroup.add(leftGuideline);
            if (rightGuideline) parentGroup.add(rightGuideline);
            if (leftPartitionTick) parentGroup.add(leftPartitionTick);
            if (rightPartitionTick) parentGroup.add(rightPartitionTick);
            internalPartitionTicks.forEach((tick) => parentGroup.add(tick));

            // Track objects for the handler
            arrowObjects.push(
                spriteMaterial,
                leftArrow,
                rightArrow,
                upArrow,
                downArrow
            );
            if (leftGuideline) arrowObjects.push(leftGuideline);
            if (rightGuideline) arrowObjects.push(rightGuideline);
            if (leftPartitionTick) arrowObjects.push(leftPartitionTick);
            if (rightPartitionTick) arrowObjects.push(rightPartitionTick);
            internalPartitionTicks.forEach((tick) => arrowObjects.push(tick));

            // Store measurement object
            measurementMap.set(measurement.id, {
                leftArrow,
                rightArrow,
                sprite,
                leftGuideline,
                rightGuideline,
                leftPartitionTick,
                rightPartitionTick,
            });
        } catch (error) {}
    });

    resolveMeasurementLabelOverlaps(labelSpriteEntries);

    // Set up visibility controls
    parentGroup.userData.updateVisibility = (show: boolean) => {
        parentGroup.visible = show;
    };

    parentGroup.userData.setGuidelinesVisible = (visible: boolean) => {
        measurementMap.forEach((components) => {
            if (components.leftGuideline)
                components.leftGuideline.visible = visible;
            if (components.rightGuideline)
                components.rightGuideline.visible = visible;
        });
    };

    parentGroup.userData.measurementMap = measurementMap;

    // Call the handler function with arrow objects
    handleArrowsAndGroup(arrowObjects, parentGroup);

    return parentGroup;
};
